import { useMemo, useCallback } from 'react';
import { useAuth } from './useAuth';
import type { Permission, UserRole, PermissionCheck } from '../types/Auth';
import { ROLE_PERMISSIONS, ROLE_HIERARCHY, hasHigherOrEqualAccess } from '../constants/roles';

/**
 * Hook for checking user permissions
 */
export function usePermissions() {
  const { user, isAuthenticated } = useAuth();

  const userPermissions = useMemo(() => {
    if (!user || !isAuthenticated) return [];
    
    // Use permissions from backend if available
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions as Permission[];
    }
    
    // Fallback to role-based permissions if backend doesn't send permissions
    if (user.roles?.length) {
      const allPermissions = new Set<Permission>();
      user.roles.forEach(roleName => {
        const rolePermissions = ROLE_PERMISSIONS[roleName as UserRole] || [];
        rolePermissions.forEach(permission => allPermissions.add(permission));
      });
      return Array.from(allPermissions);
    }
    
    return [];
  }, [user, isAuthenticated]);

  const hasPermission = useCallback((permission: Permission): boolean => {
    if (!user || !isAuthenticated) return false;
    return userPermissions.includes(permission);
  }, [user, isAuthenticated, userPermissions]);

  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    if (!user || !isAuthenticated) return false;
    return permissions.some(permission => hasPermission(permission));
  }, [user, isAuthenticated, hasPermission]);

  const hasAllPermissions = useCallback((permissions: Permission[]): boolean => {
    if (!user || !isAuthenticated) return false;
    return permissions.every(permission => hasPermission(permission));
  }, [user, isAuthenticated, hasPermission]);

  const checkPermission = useCallback((permission: Permission): PermissionCheck => {
    if (!user || !isAuthenticated) {
      return {
        hasPermission: false,
        reason: 'User is not authenticated',
      };
    }

    const hasAccess = hasPermission(permission);
    return {
      hasPermission: hasAccess,
      reason: hasAccess ? undefined : `Insufficient permissions: ${permission}`,
    };
  }, [user, isAuthenticated, hasPermission]);

  return {
    userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    checkPermission,
  };
}

/**
 * Hook for role-based operations
 */
export function useRole() {
  const { user, isAuthenticated } = useAuth();

  const userRoles = useMemo(() => {
    return user?.roles || [];
  }, [user]);

  const primaryRole = useMemo(() => {
    // Get the role with the highest hierarchy (lowest number)
    if (!user?.roles?.length) return null;
    
    const validRoles = user.roles.filter(role => role in ROLE_HIERARCHY) as UserRole[];
    if (validRoles.length === 0) return null;
    
    return validRoles.reduce((highest, current) => {
      return ROLE_HIERARCHY[current] < ROLE_HIERARCHY[highest] ? current : highest;
    });
  }, [user]);

  const hasRole = (role: UserRole): boolean => {
    if (!user || !isAuthenticated) return false;
    return user.roles?.includes(role) || false;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!user || !isAuthenticated) return false;
    return roles.some(role => hasRole(role));
  };

  const hasMinimumRole = (minimumRole: UserRole): boolean => {
    if (!user || !isAuthenticated || !primaryRole) return false;
    return hasHigherOrEqualAccess(primaryRole, minimumRole);
  };

  const isAdmin = (): boolean => {
    return hasRole('admin');
  };

  const isSystemOperator = (): boolean => {
    return hasRole('system_operator');
  };

  const isProductAdmin = (): boolean => {
    return hasRole('product_admin');
  };

  const isReinsurer = (): boolean => {
    return hasRole('reinsurer');
  };

  const isStandardUser = (): boolean => {
    return hasRole('user');
  };

  const canManageUsers = (): boolean => {
    return hasAnyRole(['admin', 'system_operator']);
  };

  const canManageProducts = (): boolean => {
    return hasAnyRole(['admin', 'product_admin']);
  };

  const canManageClaims = (): boolean => {
    return hasAnyRole(['admin', 'reinsurer']);
  };

  const canAccessSystemSettings = (): boolean => {
    return hasAnyRole(['admin', 'system_operator']);
  };

  const getRoleHierarchy = (): number => {
    if (!user || !isAuthenticated || !primaryRole) return Infinity;
    return ROLE_HIERARCHY[primaryRole];
  };

  return {
    userRoles,
    primaryRole,
    hasRole,
    hasAnyRole,
    hasMinimumRole,
    isAdmin,
    isSystemOperator,
    isProductAdmin,
    isReinsurer,
    isStandardUser,
    canManageUsers,
    canManageProducts,
    canManageClaims,
    canAccessSystemSettings,
    getRoleHierarchy,
  };
}

/**
 * Combined hook for both permissions and roles
 */
export function useRBAC() {
  const permissions = usePermissions();
  const roles = useRole();
  const { user, isAuthenticated } = useAuth();

  const canAccess = (resource: {
    permissions?: Permission[];
    roles?: UserRole[];
    requireAll?: boolean;
  }): boolean => {
    if (!user || !isAuthenticated) return false;

    const { permissions: requiredPermissions = [], roles: requiredRoles = [], requireAll = false } = resource;

    // If no requirements specified, allow access
    if (requiredPermissions.length === 0 && requiredRoles.length === 0) {
      return true;
    }

    let hasPermissionAccess = true;
    let hasRoleAccess = true;

    // Check permissions
    if (requiredPermissions.length > 0) {
      hasPermissionAccess = requireAll
        ? permissions.hasAllPermissions(requiredPermissions)
        : permissions.hasAnyPermission(requiredPermissions);
    }

    // Check roles
    if (requiredRoles.length > 0) {
      hasRoleAccess = roles.hasAnyRole(requiredRoles);
    }

    // Both conditions must be true if both are specified
    if (requiredPermissions.length > 0 && requiredRoles.length > 0) {
      return hasPermissionAccess && hasRoleAccess;
    }

    // Otherwise, either condition can be true
    return hasPermissionAccess || hasRoleAccess;
  };

  const getAccessReason = (resource: {
    permissions?: Permission[];
    roles?: UserRole[];
    requireAll?: boolean;
  }): string | null => {
    if (!user || !isAuthenticated) {
      return 'User is not authenticated';
    }

    if (canAccess(resource)) {
      return null;
    }

    const { permissions: requiredPermissions = [], roles: requiredRoles = [] } = resource;

    const reasons: string[] = [];

    if (requiredPermissions.length > 0) {
      const missingPermissions = requiredPermissions.filter(p => !permissions.hasPermission(p));
      if (missingPermissions.length > 0) {
        reasons.push(`Missing permissions: ${missingPermissions.join(', ')}`);
      }
    }

    if (requiredRoles.length > 0) {
      const hasRequiredRole = roles.hasAnyRole(requiredRoles);
      if (!hasRequiredRole) {
        reasons.push(`Requires one of these roles: ${requiredRoles.join(', ')}`);
      }
    }

    return reasons.join('; ');
  };

  return {
    ...permissions,
    ...roles,
    canAccess,
    getAccessReason,
  };
} 